// src/modals/ReviewForm.jsx
// Phase 37 Plan 06 — extracted from src/App.jsx (was lines 7467-7553 pre-Plan-37-06).
//
// Scope: Phase 27 Plan 03 Gap 3 — inline review form opened as a modal when the
// user clicks "Leave a review" on ReviewPromptCard. POSTs to review.php (server-
// side endpoint that creates a GitHub Issue labelled user-review). Draft is NOT
// persisted to localStorage — the form is ephemeral (PLAN 27-03 constraint).

import { useState } from 'react';
import { Star } from 'lucide-react';
import { getReviewDestination } from '../lib/engagement.js';

export function ReviewForm({ onClose, onSubmitted }) {
  const [stars,   setStars]   = useState(0);
  const [body,    setBody]    = useState('');
  const [status,  setStatus]  = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMsg,setErrorMsg]= useState('');

  const canSubmit = status !== 'submitting' && stars > 0 && body.trim().length >= 10;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('submitting');
    try {
      const res = await fetch(getReviewDestination(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stars, body: body.trim(), appVersion: (typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : (import.meta.env?.VITE_APP_VERSION || 'dev')) }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setStatus('success');
        if (typeof onSubmitted === 'function') onSubmitted();
        // User closes manually — no auto-dismiss timer (admin batch 1, item 2).
      } else {
        setStatus('error');
        setErrorMsg(data.error || 'Submission failed — please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Submission failed — please try again.');
    }
  }

  if (status === 'success') {
    return (
      <div style={{background:"var(--c-surface)",borderRadius:12,border:"1px solid var(--c-border)",padding:16}}>
        <div style={{textAlign:"center",padding:"24px 0"}}>
          <div role="img" aria-label={`You rated Tend ${stars} out of 5 stars`}
            style={{display:"flex",justifyContent:"center",gap:6,marginBottom:12}}>
            {[1,2,3,4,5].map(n => (
              <Star key={n} size={28}
                fill={n <= stars ? "currentColor" : "none"}
                stroke="currentColor"
                style={{color: n <= stars ? "var(--c-heading)" : "var(--c-border)"}}/>
            ))}
          </div>
          <div style={{fontSize:13,color:"var(--c-text-2)",marginBottom:10}}>You rated Tend {stars} out of 5</div>
          <div style={{fontSize:16,fontWeight:700,color:"var(--c-heading)",marginBottom:8}}>Thank you for your review!</div>
          <div style={{fontSize:'var(--font-body)',color:"var(--c-text-2)"}}>Your feedback helps us improve Tend.</div>
        </div>
        <div style={{display:"flex",justifyContent:"center"}}>
          <button type="button" onClick={onClose}
            style={{background:"var(--c-heading)",color:"#fff",border:"none",borderRadius:22,minWidth:44,minHeight:44,padding:"0 24px",fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{background:"var(--c-surface)",borderRadius:12,border:"1px solid var(--c-border)",padding:16}}>
      <div style={{fontSize:'var(--font-body)',fontWeight:700,color:"var(--c-heading)",marginBottom:14}}>Leave a review</div>
      {/* Star picker */}
      <div style={{display:"flex",gap:6,marginBottom:14}}>
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button" aria-label={`Rate ${n} star(s)`}
            onClick={() => setStars(n)}
            style={{background:"none",border:"none",cursor:"pointer",padding:2,lineHeight:1,color:n <= stars ? "var(--c-heading)" : "var(--c-border)"}}>
            <Star size={22} fill={n <= stars ? "currentColor" : "none"} stroke="currentColor"/>
          </button>
        ))}
      </div>
      {/* Body textarea */}
      <textarea
        value={body}
        onChange={e => setBody(e.target.value)}
        placeholder="What do you think of Tend? (min 10 characters)"
        maxLength={5000}
        rows={4}
        style={{width:"100%",boxSizing:"border-box",borderRadius:8,border:"1px solid var(--c-border)",padding:"10px 12px",fontSize:'var(--font-input)',fontFamily:"inherit",background:"var(--c-bg)",color:"var(--c-text)",resize:"vertical",lineHeight:1.5}} // UAT2-GAP-07 iOS zoom floor
      />
      {/* Error message */}
      {status === 'error' && (
        <div style={{fontSize:'var(--font-small)',color:"var(--c-danger)",marginTop:8}}>{errorMsg}</div>
      )}
      {/* Action buttons */}
      <div style={{display:"flex",gap:10,marginTop:12,justifyContent:"flex-end"}}>
        <button type="button" onClick={onClose}
          style={{background:"var(--c-border)",color:"var(--c-text-2)",border:"none",borderRadius:20,padding:"8px 16px",fontSize:'var(--font-small)',fontWeight:600,cursor:"pointer"}}>
          Cancel
        </button>
        <button type="button" onClick={handleSubmit} disabled={!canSubmit}
          style={{background:canSubmit?"var(--c-heading)":"var(--c-border)",color:canSubmit?"#fff":"var(--c-text-3)",border:"none",borderRadius:20,padding:"8px 16px",fontSize:'var(--font-small)',fontWeight:600,cursor:canSubmit?"pointer":"default",transition:"background 0.2s"}}>
          {status === 'submitting' ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </div>
  );
}
